import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './LoadingScreen.module.css';

/**
 * LoadingScreen - A modern, accessible loading indicator
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls visibility
 * @param {string} props.message - Loading message to display
 * @param {string} props.variant - Spinner style: 'spinner', 'dots', 'pulse', 'bar'
 * @param {string} props.size - Size: 'small', 'medium', 'large'
 * @param {string} props.backdrop - Backdrop style: 'dark', 'light', 'blur', 'transparent'
 * @param {boolean} props.overlay - Show as full-screen overlay
 * @param {boolean} props.preventClose - Prevent closing (no escape key)
 * @param {number} props.timeout - Auto-close after milliseconds
 * @param {Function} props.onTimeout - Callback when timeout reached
 */
export default function LoadingScreen({
    isOpen = false,
    message = 'Loading...',
    variant = 'spinner',
    size = 'medium',
    backdrop = 'dark',
    overlay = true,
    preventClose = true,
    timeout = null,
    onTimeout = null,
}) {
    const dialogRef = useRef(null);
    const timeoutRef = useRef(null);

    // Handle dialog open/close
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        if (isOpen) {
            dialog.showModal();
        } else {
            dialog.close();
        }
    }, [isOpen]);

    // Handle timeout
    useEffect(() => {
        if (isOpen && timeout && onTimeout) {
            timeoutRef.current = setTimeout(() => {
                onTimeout();
            }, timeout);

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [isOpen, timeout, onTimeout]);

    // Handle escape key
    useEffect(() => {
        if (!preventClose) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [preventClose]);

    if (!isOpen) return null;

    const sizeClass = styles[size] || styles.medium;
    const backdropClass = styles[`backdrop-${backdrop}`] || styles['backdrop-dark'];
    const variantClass = styles[`variant-${variant}`] || styles['variant-spinner'];

    return (
        <dialog
            ref={dialogRef}
            className={`${styles.loadingDialog} ${backdropClass} ${overlay ? styles.overlay : ''}`}
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label={message}
        >
            <div className={`${styles.content} ${sizeClass}`}>
                {/* Loading Indicator */}
                <div className={`${styles.indicator} ${variantClass}`}>
                    {variant === 'spinner' && (
                        <div className={styles.spinner}>
                            <div className={styles.spinnerCircle}></div>
                        </div>
                    )}
                    
                    {variant === 'dots' && (
                        <div className={styles.dots}>
                            <div className={styles.dot}></div>
                            <div className={styles.dot}></div>
                            <div className={styles.dot}></div>
                        </div>
                    )}
                    
                    {variant === 'pulse' && (
                        <div className={styles.pulse}>
                            <div className={styles.pulseRing}></div>
                            <div className={styles.pulseCore}></div>
                        </div>
                    )}
                    
                    {variant === 'bar' && (
                        <div className={styles.bar}>
                            <div className={styles.barProgress}></div>
                        </div>
                    )}
                </div>

                {/* Message */}
                {message && (
                    <div className={styles.message}>
                        {message}
                    </div>
                )}

                {/* Screen reader only text */}
                <span className={styles.srOnly}>Loading, please wait</span>
            </div>
        </dialog>
    );
}

LoadingScreen.propTypes = {
    isOpen: PropTypes.bool,
    message: PropTypes.string,
    variant: PropTypes.oneOf(['spinner', 'dots', 'pulse', 'bar']),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    backdrop: PropTypes.oneOf(['dark', 'light', 'blur', 'transparent']),
    overlay: PropTypes.bool,
    preventClose: PropTypes.bool,
    timeout: PropTypes.number,
    onTimeout: PropTypes.func,
};
